// 通話状態の正本（Zustand）＋通話オーケストレーション（設計v2 §2.1）。
// 状態機械: idle → dialing → active ⇄ hold → ending → ended → (閉じる) → idle
// メディア/字幕/制御の実体（クラス）は store 外のモジュール変数に持ち、store は純粋な状態のみ。

import { create } from 'zustand';
import { createCall } from './voice-api';
import { MediaSession } from './media-session';
import { SubtitleFeed } from './subtitle-feed';
import { controlChannel } from './control-channel';
import { INTERIM_FORWARD_DEFAULT } from './voice-config';
import type { ControlEvent, SubtitleLine } from './voice-types';

export type CallPhase = 'idle' | 'dialing' | 'active' | 'hold' | 'ending' | 'ended';

export interface IncomingCallInfo {
  conversationId: string;
  title?: string;
  projectId?: number;
  teamId?: number;
  source: 'push' | 'sse' | 'job-event';
}

interface CallState {
  phase: CallPhase;
  callId: string | null;
  conversationId: string | null;
  muted: boolean;
  autoForward: boolean;
  error: string | null;
  startedAt: number | null;
  endedReason: string | null;
  micLevel: number;
  spkLevel: number;
  lines: SubtitleLine[];
  partial: string | null;
  aiText: string; // streaming 中の AI 応答（確定で lines へ移す）
  aiStreaming: boolean;
  progress: string | null; // chunkKind='progress' の最新行
  incoming: IncomingCallInfo | null;
  voiceOnline: boolean; // 制御WSの接続状態
}

export const useCallStore = create<CallState>(() => ({
  phase: 'idle',
  callId: null,
  conversationId: null,
  muted: false,
  autoForward: INTERIM_FORWARD_DEFAULT,
  error: null,
  startedAt: null,
  endedReason: null,
  micLevel: 0,
  spkLevel: 0,
  lines: [],
  partial: null,
  aiText: '',
  aiStreaming: false,
  progress: null,
  incoming: null,
  voiceOnline: false,
}));

const set = useCallStore.setState;
const get = useCallStore.getState;

let media: MediaSession | null = null;
let feed: SubtitleFeed | null = null;
let lineId = 0;
let aiSeq = 0; // chat-chunk の seq 冪等 dedup
const rangIds = new Set<string>(); // 着信リングの冪等（conversationId+key）

function pushLine(who: SubtitleLine['who'], text: string, sent?: SubtitleLine['sent']): void {
  set((s) => ({ lines: [...s.lines, { id: ++lineId, who, text, at: Date.now(), sent }] }));
}

// ---- 制御WSイベント → 通話状態/字幕/着信（設計v2 §2.3/§2.6） ----
function onControlEvent(ev: ControlEvent): void {
  const s = get();
  switch (ev.type) {
    case 'chat-chunk': {
      if (ev.conversationId !== s.conversationId) return;
      if (ev.chunkKind === 'progress') {
        set({ progress: ev.delta });
        return;
      }
      if (ev.seq <= aiSeq) return; // 冪等
      aiSeq = ev.seq;
      set((st) => ({ aiText: st.aiText + ev.delta, aiStreaming: true, progress: null }));
      return;
    }
    case 'chat-message': {
      if (ev.conversationId !== s.conversationId) return;
      // whole message（chunk 無し配信）。streaming 中でなければそのまま確定行に。
      if (!get().aiStreaming && ev.text) pushLine('ai', ev.text);
      return;
    }
    case 'chat-update': {
      if (ev.conversationId !== s.conversationId) return;
      if (ev.streamState === 'final') {
        const cur = get();
        if (cur.aiText.trim() !== '') pushLine('ai', cur.aiText);
        set({ aiText: '', aiStreaming: false, progress: null });
        aiSeq = 0; // 次メッセージの seq は 1 から
        feed?.notifyTurnEnd();
      }
      return;
    }
    case 'job-event': {
      if (ev.conversationId === s.conversationId && s.phase !== 'idle') {
        feed?.notifyTurnEnd();
        set({ progress: null });
        return;
      }
      // 通話外の完了 job-event ＝将来の着信経路（現契約では届かない・設計v2 §v2-1 参考行）
      if (ev.kind === 'job-completed') {
        ringIncoming({ conversationId: ev.conversationId, source: 'job-event' }, `job:${ev.jobId ?? ''}`);
      }
      return;
    }
    case 'utterance-ack': {
      // 転送の配達確認。conversationId 未確定の新規会話はここで確定する
      if (s.conversationId == null && s.phase !== 'idle') set({ conversationId: ev.conversationId });
      set((st) => {
        const lastPending = [...st.lines].reverse().find((l) => l.who === 'operator' && l.sent === 'pending');
        if (!lastPending) return {};
        return {
          lines: st.lines.map((l) => (l.id === lastPending.id ? { ...l, sent: 'delivered' } : l)),
        };
      });
      return;
    }
    case 'error': {
      // conversationId は載らない（protocol.ts:107-111）。utteranceId 一致でターン失敗、それ以外は通話中のみ表示
      if (feed && ev.utteranceId != null && ev.utteranceId === feed.inFlightId) {
        feed.notifyTurnEnd();
        set({ error: `konecco エラー: ${ev.reason}` });
      } else if (s.phase === 'active' || s.phase === 'hold') {
        set({ error: `音声サーバエラー: ${ev.reason}` });
      }
      return;
    }
    default:
      return;
  }
}

let wired = false;
/** アプリ起動時に 1 回だけ（use-call-integration から） */
export function wireCallRuntime(): void {
  if (wired) return;
  wired = true;
  controlChannel.subscribe(onControlEvent);
  controlChannel.onStatus((connected) => set({ voiceOnline: connected }));
  controlChannel.start();
}

// ---- 着信（設計v2 §v2-1: 主=push relay / 副=SSE final / 参考=job-event） ----
export function ringIncoming(info: IncomingCallInfo, dedupeKey?: string): void {
  const s = get();
  if (s.phase !== 'idle') return; // 通話中はリングしない（自会話完了はパネル内で扱う）
  const key = `${info.conversationId}:${dedupeKey ?? info.source}`;
  if (rangIds.has(key)) return;
  rangIds.add(key);
  if (rangIds.size > 200) rangIds.clear(); // 無限成長防止（節目でリセットして冪等窓を維持）
  set({ incoming: info });
}

export function dismissIncoming(): void {
  set({ incoming: null });
}

// ---- 発信/切断/操作 ----
export async function startCall(conversationId: string | null): Promise<void> {
  if (get().phase !== 'idle') return;
  aiSeq = 0;
  set({
    phase: 'dialing',
    conversationId,
    callId: null,
    error: null,
    endedReason: null,
    startedAt: null,
    lines: [],
    partial: null,
    aiText: '',
    aiStreaming: false,
    progress: null,
    muted: false,
    incoming: null,
  });
  try {
    const alloc = await createCall(conversationId ?? undefined);
    set({ callId: alloc.callId });
    media = new MediaSession({
      onConnected: () => {
        if (get().phase === 'dialing') {
          set({ phase: 'active', startedAt: Date.now() });
          feed = new SubtitleFeed(alloc.callId, feedHandlers);
          feed.start();
        }
      },
      onMediaState: () => {},
      onClosed: () => {
        const p = get().phase;
        if (p === 'active' || p === 'hold' || p === 'dialing') endCallInternal('切断されました');
      },
      onError: (message) => set({ error: message }),
      onLevels: (mic, spk) => set({ micLevel: mic, spkLevel: spk }),
    });
    await media.start(alloc.signalUrl);
  } catch (e) {
    set({ error: e instanceof Error ? e.message : '発信に失敗しました' });
    endCallInternal('発信失敗');
  }
}

const feedHandlers = {
  onPartial: (text: string | null) => set({ partial: text }),
  onFinal: (text: string, forwarded: 'pending' | 'held' | 'off') =>
    pushLine('operator', text, forwarded === 'off' ? undefined : forwarded),
  onForwardResult: (_utteranceId: string | null, ok: boolean, detail?: string) => {
    if (!ok) set({ error: `koneccoへの送信に失敗: ${detail ?? ''}` });
  },
  onGone: () => {
    const p = get().phase;
    if (p === 'active' || p === 'hold') endCallInternal('セッションが終了しました');
  },
  isAutoForwardOn: () => get().autoForward,
};

/** 切断（drain 付き・設計v2 §2.3）: マイク停止→1.2s→最終ポーリング→bye→close */
export async function hangup(): Promise<void> {
  const s = get();
  if (s.phase !== 'active' && s.phase !== 'hold' && s.phase !== 'dialing') return;
  set({ phase: 'ending' });
  try {
    media?.setMicEnabled(false);
    if (feed) {
      await new Promise((r) => window.setTimeout(r, 1200));
      await feed.pollOnce();
    }
  } finally {
    endCallInternal('切断しました');
  }
}

function endCallInternal(reason: string): void {
  feed?.stop();
  feed = null;
  media?.stop(true);
  media = null;
  set({ phase: 'ended', endedReason: reason, partial: null, aiStreaming: false, micLevel: 0, spkLevel: 0 });
}

/** ended パネルを閉じて idle へ */
export function closeCallPanel(): void {
  if (get().phase !== 'ended') return;
  set({ phase: 'idle', callId: null, conversationId: null, error: null });
}

export function toggleMute(): void {
  const s = get();
  if (!media || (s.phase !== 'active' && s.phase !== 'hold')) return;
  const muted = !s.muted;
  if (s.phase === 'active') media.setMicEnabled(!muted);
  set({ muted });
}

/** ホールド（FE セマンティクス: 上り停止＋下り再生停止。VS 側保留音/進捗喋りは #212 の領分） */
export function toggleHold(): void {
  const s = get();
  if (!media) return;
  if (s.phase === 'active') {
    media.setMicEnabled(false);
    media.setSpeakerMuted(true);
    set({ phase: 'hold' });
  } else if (s.phase === 'hold') {
    media.setMicEnabled(!s.muted);
    media.setSpeakerMuted(false);
    set({ phase: 'active' });
  }
}

export function setAutoForward(v: boolean): void {
  set({ autoForward: v });
}
